import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useSocratesV2 } from '../context/SocratesV2Context';
import { useSocratesApi } from './useSocratesApi';
import socratesApi from '../api/socratesApi';
import socratesLegacyApi from '../api/socratesLegacyApi';

/**
 * Socrates Interview 비즈니스 로직 Hook
 * 면접 생성, 로드, 답변 제출, 재시작 등의 비즈니스 로직 처리
 */
export const useSocratesInterview = () => {
  const { state, dispatch } = useSocratesV2();
  const { error, executeApi } = useSocratesApi();

  // Context의 isLoading을 사용하여 컴포넌트 간 로딩 상태 공유
  const loading = state.isLoading;
  // 채팅 로딩 상태 (로딩 버블 표시용)
  const chatLoading = state.isChatLoading;

  /**
   * 면접 생성
   * @param {FormData} formData - 면접 생성 폼 데이터
   * @returns {Promise<{interviewId: number}>}
   */
  const createInterview = useCallback(async (formData) => {
    // 면접 생성만 수행하고, 상세 조회는 navigate 후 useEffect에서 처리
    const result = await executeApi(() => socratesApi.createInterview(formData));
    return result;
  }, [executeApi]);

  /**
   * 하이브리드 면접 생성
   * 한교파 API + 레거시 API 를 순차적으로 호출하여 면접 생성
   *
   * 흐름:
   * 1. 한교파 API로 인터뷰 생성 → interviewId 획득
   * 2. 레거시 그룹 생성 (한교파 interviewId 매핑)
   * 3. 레거시 폼 생성 (한교파 interviewId 포함)
   * 4. 레거시 시작 API 호출
   * 5. 결과 반환 (groupId, interviewId)
   *
   * @param {FormData} formData - 면접 생성 폼 데이터
   * @param {Object} legacyParams - 레거시 API에 필요한 추가 파라미터
   * @param {string} [legacyParams.title] - 면접 제목
   * @param {string} [legacyParams.fileContent] - 파일 내용
   * @param {string} [legacyParams.clarioPrompt] - 클라리오 프롬프트
   * @param {string} [legacyParams.style] - 스타일
   * @param {string} [legacyParams.likeTopic] - 관심 주제
   * @param {number} [legacyParams.mainMaxCount] - 주요 질문 수
   * @param {number} [legacyParams.fuMaxCount] - 후속 질문 수
   * @param {boolean} [legacyParams.showResult] - 결과 표시 여부
   * @param {string} legacyParams.userId - 사용자 ID
   * @param {string} [legacyParams.persona] - 페르소나
   * @returns {Promise<{interviewId: number, groupId: number}>}
   */
  const createHybridInterview = useCallback(async (formData, legacyParams) => {
    // 1. 한교파 API로 인터뷰 생성
    const hankyopaResult = await executeApi(() => socratesApi.createInterview(formData));
    const hankyopaInterviewId = hankyopaResult.interviewId;

    if (!hankyopaInterviewId) {
      throw new Error('한교파 인터뷰 생성 실패: interviewId가 없습니다.');
    }

    console.log('[Hybrid] 한교파 인터뷰 생성 완료:', hankyopaInterviewId);

    // 2. 레거시 그룹 생성 (한교파 interviewId 매핑)
    const groupResult = await socratesLegacyApi.createGroup({
      title: legacyParams.title || '새 대화',
      hgpInterviewId: hankyopaInterviewId,
    });

    const groupId = groupResult.id;
    if (!groupId) {
      throw new Error('레거시 그룹 생성 실패: groupId가 없습니다.');
    }

    console.log('[Hybrid] 레거시 그룹 생성 완료:', groupId);

    // 3. 레거시 폼 생성 (한교파 interviewId 포함)
    await socratesLegacyApi.createForm({
      groupId,
      fileContent: legacyParams.fileContent,
      clarioPrompt: legacyParams.clarioPrompt,
      style: legacyParams.style,
      likeTopic: legacyParams.likeTopic,
      expTitle: legacyParams.title,
      mainMaxCount: legacyParams.mainMaxCount,
      fuMaxCount: legacyParams.fuMaxCount,
      showResult: legacyParams.showResult,
      userId: legacyParams.userId,
      persona: legacyParams.persona,
      hgpInterviewId: hankyopaInterviewId,
    });

    console.log('[Hybrid] 레거시 폼 생성 완료');

    // 4. 레거시 시작 API 호출
    await socratesLegacyApi.startInterview(groupId);
    console.log('[Hybrid] 레거시 면접 시작 완료');

    // 5. 결과 반환 (한교파 interviewId를 메인으로 사용)
    return {
      interviewId: hankyopaInterviewId,
      groupId,
    };
  }, [executeApi]);

  /**
   * 면접 로드 (상세 정보 + 메시지 목록)
   * @param {number} interviewId
   * @returns {Promise<Interview>}
   */
  const loadInterview = useCallback(async (interviewId) => {
    const interview = await executeApi(() => socratesApi.getInterview(interviewId));
    dispatch({ type: 'SET_INTERVIEW', payload: interview });

    const { messages } = await executeApi(() => socratesApi.getMessages(interviewId));
    dispatch({ type: 'SET_MESSAGES', payload: messages });

    return interview;
  }, [executeApi, dispatch]);

  /**
   * 답변 제출 및 다음 질문 받기
   * @param {string} content - 사용자 답변 내용
   * @returns {Promise<{messageId: number, responseContent: string}>}
   */
  const submitAnswer = useCallback(async (content) => {
    if (!state.currentInterview) {
      throw new Error('면접 정보가 없습니다.');
    }

    const userId = state.currentInterview.userId;
    const interviewId = state.currentInterview.id;

    // 낙관적 업데이트: 사용자 메시지 먼저 추가
    const tempUserMessage = {
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
      id: `temp-${Date.now()}`
    };

    dispatch({
      type: 'ADD_MESSAGE',
      payload: tempUserMessage
    });

    // 로딩 상태 시작 (Context에 설정하여 공유)
    dispatch({ type: 'SET_LOADING', payload: true });
    // 채팅 로딩 상태 (로딩 버블 표시용)
    dispatch({ type: 'SET_CHAT_LOADING', payload: true });

    // AbortController 생성 및 저장 (정지 버튼 클릭 시 요청 취소용)
    const controller = new AbortController();
    dispatch({ type: 'SET_ABORT_CONTROLLER', payload: controller });

    try {
      // API 호출 (AbortController signal 전달)
      const result = await executeApi(() =>
        socratesApi.chat(interviewId, userId, content, controller.signal)
      );

      // 정지 상태가 아닐 때만 AI 응답 추가 (정지 버튼 클릭 시 메시지 추가 방지)
      if (!state.isPausing) {
        // AI 응답 추가
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            id: result.messageId,
            role: 'assistant',
            content: result.responseContent,
            createdAt: new Date().toISOString()
          }
        });

        // AI 응답이 추가되면 즉시 채팅 로딩 버블 숨기기
        dispatch({ type: 'SET_CHAT_LOADING', payload: false });

        // 면접 상태 업데이트 (완료 여부 확인) - 메시지 목록은 다시 불러오지 않음 (낙관적 업데이트 유지)
        const updatedInterview = await executeApi(() => socratesApi.getInterview(interviewId));
        dispatch({ type: 'SET_INTERVIEW', payload: updatedInterview });

        // 면접이 완료되면 즉시 버튼 표시 (결과 생성은 결과보기 버튼 클릭 시 수행)
        if (updatedInterview?.status === 'completed') {
          console.log('면접 완료 - 결과 생성은 결과보기 버튼 클릭 시 수행');
        }
      }

      return result;
    } catch (error) {
      // AbortError는 사용자가 정지한 경우 - 에러 무시
      if (error.name === 'AbortError') {
        console.log('API 요청이 취소되었습니다.');
        return null;
      }
      // 에러 발생 시 낙관적 업데이트 롤백
      dispatch({
        type: 'SET_MESSAGES',
        payload: state.messages.filter(msg => msg.id !== tempUserMessage.id)
      });
      throw error;
    } finally {
      // AbortController 정리
      dispatch({ type: 'SET_ABORT_CONTROLLER', payload: null });
      // 로딩 상태 종료 (정지 상태가 아닐 때만)
      if (!state.isPausing) {
        dispatch({ type: 'SET_LOADING', payload: false });
        dispatch({ type: 'SET_CHAT_LOADING', payload: false });
      }
    }
  }, [state.currentInterview, state.messages, state.isPausing, executeApi, dispatch, loadInterview]);

  /**
   * 면접 재시작
   * @returns {Promise<void>}
   */
  const restartInterview = useCallback(async () => {
    if (!state.currentInterview) {
      throw new Error('면접 정보가 없습니다.');
    }

    const interviewId = state.currentInterview.id;
    const userId = state.currentInterview.userId;

    // 면접 재시작 로딩 상태 시작 (전체 화면 로딩)
    dispatch({ type: 'SET_RESTARTING', payload: true });

    try {
      await executeApi(() => socratesApi.restartInterview(interviewId, userId));
      await loadInterview(interviewId);
    } finally {
      // 면접 재시작 로딩 상태 종료
      dispatch({ type: 'SET_RESTARTING', payload: false });
    }
  }, [state.currentInterview, executeApi, dispatch, loadInterview]);

  /**
   * 결과 생성
   * @returns {Promise<void>}
   */
  const generateResult = useCallback(async () => {
    if (!state.currentInterview) {
      throw new Error('면접 정보가 없습니다.');
    }

    const interviewId = state.currentInterview.id;

    // 결과 생성 로딩 상태 시작 (전체 화면 로딩)
    // flushSync를 사용하여 상태 업데이트를 동기적으로 처리하여 로딩 화면이 즉시 표시되도록 함
    flushSync(() => {
      dispatch({ type: 'SET_GENERATING_RESULT', payload: true });
    });

    // 브라우저가 화면을 그릴 시간을 확보 (로딩 화면이 표시된 후 API 호출)
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    try {
      await executeApi(() => socratesApi.generateResult(interviewId));
      await loadInterview(interviewId);
    } finally {
      dispatch({ type: 'SET_GENERATING_RESULT', payload: false });
    }
  }, [state.currentInterview, executeApi, loadInterview, dispatch]);

  /**
   * 면접 복제
   * @returns {Promise<{interviewId: number}>}
   */
  const cloneInterview = useCallback(async () => {
    if (!state.currentInterview) {
      throw new Error('면접 정보가 없습니다.');
    }

    const interviewId = state.currentInterview.id;
    const userId = state.currentInterview.userId;

    // 면접 복제 로딩 상태 시작 (전체 화면 로딩)
    dispatch({ type: 'SET_RESTARTING', payload: true });

    try {
      const result = await executeApi(() => socratesApi.cloneInterview(interviewId, userId));
      // 네비게이션 후 새 페이지에서 로딩 상태 관리하므로 여기서는 해제하지 않음
      return result;
    } catch (error) {
      // 에러 발생 시 로딩 상태 해제
      dispatch({ type: 'SET_RESTARTING', payload: false });
      throw error;
    }
  }, [state.currentInterview, executeApi, dispatch]);

  /**
   * 면접 일시정지
   * 질문 생성 중에도 즉시 로딩 상태를 종료하고 면접을 일시정지
   * API 요청을 취소하고 인터뷰 상태만 업데이트 (메시지는 유지)
   * @returns {Promise<void>}
   */
  const pauseInterview = useCallback(async () => {
    if (!state.currentInterview) {
      throw new Error('면접 정보가 없습니다.');
    }

    const interviewId = state.currentInterview.id;
    const userId = state.currentInterview.userId;

    // 정지 플래그 설정 (메시지 추가 방지)
    dispatch({ type: 'SET_PAUSING', payload: true });

    // 진행 중인 API 요청 취소
    if (state.abortController) {
      state.abortController.abort();
    }

    // 즉시 로딩 상태 종료 (질문 생성 중단 효과)
    dispatch({ type: 'SET_LOADING', payload: false });
    dispatch({ type: 'SET_CHAT_LOADING', payload: false });

    // 낙관적 업데이트: 인터뷰 상태를 즉시 "paused"로 설정
    // (타이머/면접중단 버튼이 잠깐 나타나는 것 방지)
    dispatch({
      type: 'SET_INTERVIEW',
      payload: { ...state.currentInterview, status: 'paused' }
    });

    try {
      // pause API 호출
      await executeApi(() => socratesApi.pauseInterview(interviewId, userId));
      // 서버에서 최신 인터뷰 상태 확인 (메시지는 유지)
      const updatedInterview = await executeApi(() => socratesApi.getInterview(interviewId));
      dispatch({ type: 'SET_INTERVIEW', payload: updatedInterview });
    } finally {
      // 정지 플래그 해제
      dispatch({ type: 'SET_PAUSING', payload: false });
    }
  }, [state.currentInterview, state.abortController, executeApi, dispatch]);

  /**
   * 면접 재개
   * @returns {Promise<void>}
   */
  const resumeInterview = useCallback(async () => {
    if (!state.currentInterview) {
      throw new Error('면접 정보가 없습니다.');
    }

    const interviewId = state.currentInterview.id;
    const userId = state.currentInterview.userId;

    // 로딩 상태 시작
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      await executeApi(() => socratesApi.resumeInterview(interviewId, userId));
      await loadInterview(interviewId);
    } finally {
      // 로딩 상태 종료
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.currentInterview, executeApi, dispatch, loadInterview]);

  /**
   * 면접 삭제
   * @returns {Promise<void>}
   */
  const deleteInterview = useCallback(async () => {
    if (!state.currentInterview) {
      throw new Error('면접 정보가 없습니다.');
    }

    const interviewId = state.currentInterview.id;
    await executeApi(() => socratesApi.deleteInterview(interviewId));

    // 상태 초기화
    dispatch({ type: 'RESET' });
  }, [state.currentInterview, executeApi, dispatch]);

  /**
   * 상태 초기화
   */
  const resetState = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  // 면접 재시작 로딩 상태
  const isRestarting = state.isRestarting;
  // 결과 생성 로딩 상태
  const isGeneratingResult = state.isGeneratingResult;

  return {
    // 상태
    interview: state.currentInterview,
    messages: state.messages,
    loading,
    chatLoading,
    isRestarting,
    isGeneratingResult,
    error,

    // 액션
    createInterview,
    createHybridInterview,
    loadInterview,
    submitAnswer,
    restartInterview,
    generateResult,
    cloneInterview,
    pauseInterview,
    resumeInterview,
    deleteInterview,
    resetState
  };
};
